import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { fetchEvent } from '../api/public'
import { EventGallery } from '../components/EventGallery'
import { SlideTitle } from '../components/SlideTitle'
import { useCrtPageTitle } from '../hooks/useCrtPageTitle'
import { usePageMeta } from '../hooks/usePageMeta'
import { cmsImageSocialUrl } from '../lib/cms-image'
import { splitTitleForCrt } from '../lib/event-format'
import {
  schemaBreadcrumb,
  schemaEventFaq,
  schemaGraph,
  schemaMusicEvent,
  schemaWebPage,
} from '../lib/schema-org'

function mapsSearchUrl(line: string) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(line)}`
}

function guestLink(g: { websiteUrl?: string | null; spotifyUrl?: string | null; soundcloudUrl?: string | null }): string | null {
  return g.websiteUrl || g.spotifyUrl || g.soundcloudUrl || null
}

function bodyHasContent(html: string | null): boolean {
  if (!html) return false
  const text = html.replace(/<[^>]+>/g, '').replace(/&[a-z#0-9]+;/gi, ' ').replace(/\s+/g, ' ').trim()
  if (text.length < 30) return false
  const lo = text.toLowerCase()
  return !lo.startsWith('add your') && !lo.startsWith('event details') && !lo.includes('add your custom')
}

export function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const q = useQuery({
    queryKey: ['event', slug],
    queryFn: () => fetchEvent(slug!),
    enabled: !!slug,
  })

  const ev = q.data
  useCrtPageTitle(ev?.title ?? null)

  const isPast = ev?.displayStatus === 'past'
  const isCancelled = ev?.displayStatus === 'cancelled'
  const isUpcoming = !isPast && !isCancelled

  const tz = ev?.timezone ?? 'Europe/Zurich'
  const dateLine = ev
    ? new Intl.DateTimeFormat('de-CH', { dateStyle: 'long', timeZone: tz }).format(new Date(ev.startsAt))
    : ''
  const timeLine = ev
    ? new Intl.DateTimeFormat('de-CH', { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(new Date(ev.startsAt))
    : ''
  const endsLine = ev?.endsAt
    ? new Intl.DateTimeFormat('de-CH', { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(new Date(ev.endsAt))
    : null
  const place = ev?.venueName || ev?.locationLabel || null
  const hasStructuredLineup = Boolean(ev?.lineupArtists?.length) || Boolean(ev?.guestArtists?.length)
  const statusKicker = isCancelled ? 'ABGESAGT' : isPast ? 'ARCHIV' : 'UPCOMING'
  const titleLines = ev ? splitTitleForCrt(ev.title, 3) : ['…']

  const detailPath = slug ? `/events/${slug}` : '/events'
  const jsonLd = useMemo(() => {
    if (!ev || !slug) return null
    const crumbs = schemaBreadcrumb(
      [
        { name: 'Home', path: '/' },
        { name: 'Events', path: '/events' },
        { name: ev.title, path: detailPath },
      ],
      detailPath,
    )
    const event = schemaMusicEvent(ev, { detailPath, ticketUrl: ev.ticketUrl })
    const page = schemaWebPage({
      path: detailPath,
      name: ev.title,
      description: ev.summary?.trim() || undefined,
      pageType: 'ItemPage',
      image: ev.featuredImageUrl,
      breadcrumb: crumbs,
      mainEntity: { '@id': event['@id'] as string },
    })
    const faq = ev.faqJson?.length ? schemaEventFaq(ev.faqJson) : null
    return schemaGraph(crumbs, page, event, ...(faq ? [faq] : []))
  }, [ev, slug, detailPath])

  usePageMeta(
    ev
      ? {
          title: ev.title,
          description: ev.summary?.trim() || `${ev.title} — BNKR Event.`,
          canonical: detailPath,
          ogImage: cmsImageSocialUrl(ev.featuredImageUrl) ?? ev.featuredImageUrl,
          ogType: 'website',
          jsonLd,
        }
      : null,
  )

  if (!slug) return null

  return (
    <>
      {q.isLoading && (
        <div className="wrap" style={{ paddingTop: 'var(--sp-8)' }}>
          <p className="muted" style={{ fontFamily: 'var(--mono)', letterSpacing: '.14em', fontSize: 13 }}>LOADING…</p>
        </div>
      )}
      {q.isError && (
        <div className="wrap" style={{ paddingTop: 'var(--sp-8)' }}>
          <p className="error">Event nicht gefunden.</p>
        </div>
      )}

      {ev && (
        <>
          {/* ── CRT HERO ── always gradient bg, image shown separately below */}
          <section className="ev-crt-hero">
            <div className="ev-crt-hero__bg ev-crt-hero__bg--gradient" />
            <div className="crt-fx" aria-hidden />
            <div className="crt-sweep" aria-hidden />

            <div className="wrap ev-crt-hero__inner">
              <div className="ev-crt-hero__top">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="ev-back-btn"
                  aria-label="Zurück"
                >
                  ← Events
                </button>
                <span className="ev-crt-status-badge">
                  {isCancelled && <span style={{ color: '#ff6b6b' }}>ABGESAGT · </span>}
                  {statusKicker}
                </span>
              </div>

              <SlideTitle lines={titleLines} />

              <div className="slide-meta" style={{ marginTop: 'clamp(10px, 2vw, 18px)' }}>
                <span className="pill">{dateLine}</span>
                {timeLine && <span>{timeLine}{endsLine ? `–${endsLine}` : ''} Uhr</span>}
                {place && <span>{place.toUpperCase()}</span>}
              </div>

              {isUpcoming && (ev.ticketUrl || ev.addressLine?.trim()) && (
                <div className="slide-cta">
                  {ev.ticketUrl && (
                    <a className="btn-solid-crt" href={`/events/${ev.slug}/tickets`}>
                      Tickets kaufen →
                    </a>
                  )}
                  {ev.addressLine?.trim() && (
                    <a className="btn-ghost-crt" href={mapsSearchUrl(ev.addressLine.trim())} target="_blank" rel="noreferrer">
                      Auf Karte zeigen
                    </a>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ── CONTENT ── */}
          <section id="event-content" className="section" style={{ paddingBottom: 'var(--sp-16)' }}>
            <div className="wrap">
              {hasStructuredLineup && (
                <div className="ev-detail-lineup">
                  <div className="kicker" style={{ marginBottom: 14 }}>Line-up</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {ev.lineupArtists.map((a) => (
                      <Link key={a.id} to={`/artists/${a.slug}`} className="chip chip-crt">
                        {a.name}
                      </Link>
                    ))}
                    {ev.guestArtists.map((g, i) => {
                      const href = guestLink(g)
                      return href ? (
                        <a key={`g-${i}`} href={href} target="_blank" rel="noreferrer noopener" className="chip">{g.name}</a>
                      ) : (
                        <span key={`g-${i}`} className="chip">{g.name}</span>
                      )
                    })}
                  </div>
                </div>
              )}

              {!hasStructuredLineup && ev.lineupText?.trim() && (
                <div className="ev-detail-lineup">
                  <div className="kicker" style={{ marginBottom: 10 }}>Line-up</div>
                  <p style={{ color: 'var(--ink-2)', fontFamily: 'var(--mono)', fontSize: 13 }}>{ev.lineupText.trim()}</p>
                </div>
              )}

              {isCancelled && (
                <p className="muted" style={{ marginBottom: 'var(--sp-5)' }}>Dieses Event wurde abgesagt.</p>
              )}

              {ev.summary?.trim() && (
                <p className="event-detail-lead" style={{ marginBottom: 'var(--sp-6)' }}>
                  {ev.summary.trim()}
                </p>
              )}

              {bodyHasContent(ev.bodyHtml) && (
                <div className="rich-text rich-text--paper" dangerouslySetInnerHTML={{ __html: ev.bodyHtml! }} />
              )}

              {ev.guestArtists?.some((g) => g.bioHtml) && (
                <div style={{ marginTop: 'var(--sp-8)' }}>
                  {ev.guestArtists.map((g, i) =>
                    g.bioHtml ? (
                      <div key={`gb-${i}`} className="rich-text" style={{ marginBottom: 'var(--sp-5)' }}>
                        <strong style={{ display: 'block', marginBottom: 'var(--sp-2)' }}>
                          {(() => { const href = guestLink(g); return href ? <a href={href} target="_blank" rel="noreferrer noopener">{g.name}</a> : g.name })()}
                        </strong>
                        <div dangerouslySetInnerHTML={{ __html: g.bioHtml }} />
                      </div>
                    ) : null
                  )}
                </div>
              )}

              {ev.galleryUrls?.length ? (
                <EventGallery urls={ev.galleryUrls} title={isPast ? 'Impressionen' : 'Galerie'} />
              ) : null}
            </div>
          </section>
        </>
      )}
    </>
  )
}
